import React, { useState, useEffect, useRef } from 'react';
import { safeLocalStorage } from '@/lib/storage-utils';
import { Plus } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { supabase } from '@/lib/supabase';
import { WorkspaceGridCard } from './WorkspaceGridCard';
import { WorkspaceEditor } from './WorkspaceEditor';
import { Workspace } from '@/context/WorkspaceContext';

interface WorkspaceSettingsProps {
  isGlobalDarkMode: boolean;
  onWorkspaceSelect?: (workspace: Workspace | null) => void;
  selectedWorkspace?: Workspace | null;
  workspaceTab?: 'general' | 'appearance' | 'members' | 'roles-permissions' | 'integrations';
  onWorkspaceTabChange?: (tab: 'general' | 'appearance' | 'members' | 'roles-permissions' | 'integrations') => void;
}

export const WorkspaceSettings: React.FC<WorkspaceSettingsProps> = ({
  isGlobalDarkMode,
  onWorkspaceSelect,
  selectedWorkspace: externalSelectedWorkspace,
  workspaceTab,
  onWorkspaceTabChange
}) => {
  const { user, profile } = useAuth();
  const { setCurrentWorkspace: setContextWorkspace } = useWorkspace();
  const [internalSelectedWorkspace, setInternalSelectedWorkspace] = useState<Workspace | null>(null);
  const selectedWorkspace = externalSelectedWorkspace !== undefined ? externalSelectedWorkspace : internalSelectedWorkspace;
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const previousWorkspaceRef = useRef<Workspace | null>(null);
  const userClickedBackRef = useRef(false);

  // Load workspaces from database
  useEffect(() => {
    if (!user || !profile) return;
    loadWorkspaces();
  }, [user, profile]);

  // Track when workspace changes to null (back button clicked)
  useEffect(() => {
    if (previousWorkspaceRef.current && !selectedWorkspace) {
      userClickedBackRef.current = true;
    }
    previousWorkspaceRef.current = selectedWorkspace;
  }, [selectedWorkspace]);

  // Reset back flag when leaving members tab
  useEffect(() => {
    if (workspaceTab !== 'members') {
      userClickedBackRef.current = false;
    }
  }, [workspaceTab]);

  // Auto-select current workspace when navigating to members tab
  useEffect(() => {
    if (
      workspaceTab === 'members' &&
      !selectedWorkspace &&
      !userClickedBackRef.current &&
      currentWorkspaceId &&
      workspaces.length > 0
    ) {
      const currentWorkspace = workspaces.find(w => w.id === currentWorkspaceId);
      if (currentWorkspace) {
        if (onWorkspaceSelect) {
          onWorkspaceSelect(currentWorkspace);
        } else {
          setInternalSelectedWorkspace(currentWorkspace);
        }
      }
    }
  }, [workspaceTab, selectedWorkspace, currentWorkspaceId, workspaces, onWorkspaceSelect]);

  const loadWorkspaces = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Get all workspaces where user is a member
      const { data: memberData, error: memberError } = await supabase
        .from('workspace_members')
        .select('workspace_id, role')
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (memberError) throw memberError;

      if (!memberData || memberData.length === 0) {
        // Create default workspace if user has no workspaces
        await createDefaultWorkspace();
        return;
      }

      // Load workspace details
      const workspaceIds = memberData.map(m => m.workspace_id);
      const { data: workspacesData, error: workspacesError } = await supabase
        .from('workspaces')
        .select('*')
        .in('id', workspaceIds)
        .order('created_at', { ascending: true });

      if (workspacesError) throw workspacesError;

      // Get current workspace from localStorage
      const savedCurrentId = safeLocalStorage.getItem('currentWorkspaceId');
      const currentId = savedCurrentId && workspaceIds.includes(savedCurrentId)
        ? savedCurrentId
        : workspaceIds[0];

      setCurrentWorkspaceId(currentId);
      safeLocalStorage.setItem('currentWorkspaceId', currentId);

      // Create a map of workspace_id to user role
      const roleMap = memberData.reduce((acc, m) => {
        acc[m.workspace_id] = m.role;
        return acc;
      }, {} as Record<string, string>);

      const mappedWorkspaces = (workspacesData || []).map((w: any) => ({
        ...w,
        isCurrent: w.id === currentId,
        userRole: roleMap[w.id] as 'owner' | 'admin' | 'manager' | 'member'
      })) as Workspace[];

      // Sort workspaces so the active one is first
      const sortedWorkspaces = mappedWorkspaces.sort((a, b) => {
        if (a.isCurrent) return -1;
        if (b.isCurrent) return 1;
        return 0;
      });

      setWorkspaces(sortedWorkspaces);
    } catch (err: any) {
      console.error('Error loading workspaces:', err);
    } finally {
      setLoading(false);
    }
  };

  const createDefaultWorkspace = async () => {
    if (!user || !profile) return;

    try {
      const firstName = profile.first_name || 'User';
      // Use a unique ID instead of the hardcoded 'default-workspace'
      const workspaceId = `workspace-${crypto.randomUUID()}`;
      const workspaceName = `${firstName}'s Workspace`;

      // Create workspace in database
      const { error: workspaceError } = await supabase
        .from('workspaces')
        .insert({
          id: workspaceId,
          name: workspaceName,
          color: getComputedStyle(document.documentElement).getPropertyValue('--workspace-accent').trim() || '#3B82F6',
          created_by: user.id
        });

      if (workspaceError) {
        throw workspaceError;
      }

      // Add user as owner
      const { error: memberError } = await supabase
        .from('workspace_members')
        .insert({
          workspace_id: workspaceId,
          user_id: user.id,
          role: 'owner',
          status: 'active'
        });

      if (memberError) {
        throw memberError;
      }

      // Reload workspaces
      await loadWorkspaces();
    } catch (err: any) {
      console.error('Error creating default workspace:', err);
      setLoading(false);
    }
  };

  const handleWorkspaceClick = (workspace: Workspace) => {
    // Allow access for members (they can view Members tab), managers, admins, and owners
    const canAccess = workspace.userRole && ['member', 'manager', 'admin', 'owner'].includes(workspace.userRole);

    if (!canAccess) {
      return; // Do nothing if user doesn't have permission
    }

    // Only allow accessing the active workspace
    if (workspace.isCurrent) {
      if (onWorkspaceSelect) {
        onWorkspaceSelect(workspace);
      } else {
        setInternalSelectedWorkspace(workspace);
      }
    }
    // For non-active workspaces, do nothing (they need to switch first)
  };

  const handleCreateNew = async () => {
    if (!user) return;

    // Restriction: Only owners can create new workspaces
    // Unless they have NO workspaces (handled by loadWorkspaces -> createDefaultWorkspace)
    const canCreate = workspaces.length === 0 || workspaces.some(w => w.userRole === 'owner');
    
    if (!canCreate) {
      alert('Only workspace owners can create additional workspaces.');
      return;
    }

    try {
      const workspaceId = `workspace-${crypto.randomUUID()}`;
      const newWorkspace = {
        id: workspaceId,
        name: 'New Workspace',
        color: getComputedStyle(document.documentElement).getPropertyValue('--workspace-accent').trim() || '#3B82F6',
        created_by: user.id
      };

      // Create workspace in database
      const { error: workspaceError } = await supabase
        .from('workspaces')
        .insert(newWorkspace);

      if (workspaceError) throw workspaceError;

      // Add user as owner
      const { error: memberError } = await supabase
        .from('workspace_members')
        .insert({
          workspace_id: workspaceId,
          user_id: user.id,
          role: 'owner',
          status: 'active'
        });

      if (memberError) throw memberError;

      // Switch to the new workspace
      setCurrentWorkspaceId(workspaceId);
      safeLocalStorage.setItem('currentWorkspaceId', workspaceId);

      // Update the WorkspaceContext
      await setContextWorkspace({ ...newWorkspace, isCurrent: true } as any);

      // Reload workspaces with the new one as current
      await loadWorkspaces();

      // Select the new workspace for editing
      if (onWorkspaceSelect) {
        onWorkspaceSelect({ ...newWorkspace, isCurrent: true });
      } else {
        setInternalSelectedWorkspace({ ...newWorkspace, isCurrent: true });
      }
    } catch (err: any) {
      console.error('Error creating workspace:', err);
      alert('Failed to create workspace. Please try again.');
    }
  };

  const handleDelete = async (workspaceId: string) => {
    if (!user) return;

    if (workspaceId === currentWorkspaceId) {
      alert('Cannot delete the current workspace. Please switch to another workspace first.');
      return;
    }

    if (!confirm('Are you sure you want to delete this workspace? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('workspaces')
        .delete()
        .eq('id', workspaceId);

      if (error) throw error;

      await loadWorkspaces();
    } catch (err: any) {
      console.error('Error deleting workspace:', err);
      alert('Failed to delete workspace. Please try again.');
    }
  };

  const handleSwitchWorkspace = async (workspaceId: string) => {
    const workspaceToSwitch = workspaces.find(w => w.id === workspaceId);
    if (!workspaceToSwitch) return;

    const updated = workspaces.map(w => ({
      ...w,
      isCurrent: w.id === workspaceId
    }));

    // Sort workspaces so the active one is first
    const sortedWorkspaces = updated.sort((a, b) => {
      if (a.isCurrent) return -1;
      if (b.isCurrent) return 1;
      return 0;
    });

    setWorkspaces(sortedWorkspaces);
    setCurrentWorkspaceId(workspaceId);
    safeLocalStorage.setItem('currentWorkspaceId', workspaceId);

    // Update the WorkspaceContext so the sidebar updates
    await setContextWorkspace(workspaceToSwitch as any);
  };

  // If a workspace is selected, show the editor
  if (selectedWorkspace) {
    return (
      <WorkspaceEditor
        workspace={selectedWorkspace}
        isGlobalDarkMode={isGlobalDarkMode}
        activeTab={workspaceTab || 'general'}
        onTabChange={onWorkspaceTabChange}
      />
    );
  }

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">
            Loading workspaces...
          </p>
        </div>
      </div>
    );
  }

  return null;
};
